import { cn } from '@/lib/utils/utils';
import hero7 from '../../assets/hero7.png';
import { useDarkMode } from '../hooks/use-dark-mode';
import { usePath } from 'crossroad';
import { useRef, useMemo, use, useEffect, useCallback, useState } from 'react';
import { useZoomGestures } from '../hooks/use-zoom-gestures';
import { containsPassage, findPathToNode } from '../outline/outline-tree-utils';
import { type BibleBook } from '@/lib/utils/bible-utils';
import { IcicleChart } from '@/components/context/IcicleChart';
import { CanonRail } from '@/components/context/CanonRail';
import { BookRail } from '@/components/context/BookRail';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { motion, LayoutGroup } from 'framer-motion';
import { ContextBreadcrumbs } from '@/lib/canon-context/context-breadcrumbs';

interface CanonContextViewProps {
	outline: Outline;
	currentNode?: OutlineNode;
	highlightedPassageId: string;
	canZoomOut: boolean;
	onNavigateToNode: (nodeId: string) => void;
	onNavigateToNodeContext: (nodeId: string) => void;
	onNavigateToParent: () => void;
	onNavigateBackIn: () => void;
	onBookSelect: (book: BibleBook) => void;
	children: React.ReactNode;
}

export function CanonContextView({outline, currentNode, highlightedPassageId, canZoomOut, onNavigateToNode, onNavigateToNodeContext, onNavigateToParent, onNavigateBackIn, onBookSelect}: CanonContextViewProps) {
	const isDarkMode = useDarkMode();
	const [, setPath] = usePath();
	const hasCurrentNode = currentNode !== undefined;
    const { data: outlineData } = use(OutlineDataContext);
    
    // Handler to close context view and return to passage
    const handleClose = useCallback(() => {
        setPath(`/app/passage/${highlightedPassageId}`);
    }, [highlightedPassageId, setPath]);
    
    // Ref for auto-scrolling to icicle chart
    const icicleContainerRef = useRef<HTMLDivElement>(null);
    
    // Auto-scroll to top when navigating to a new node
    useEffect(() => {
        if (icicleContainerRef.current && currentNode) {
            icicleContainerRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
            });
        }
    }, [currentNode?.id]);
    const canonVerseTotals = useMemo(() => {
        const totals: Partial<Record<BibleBook, number>> = {};
        const items = outlineData?.items ? Object.values(outlineData.items) as Outline[] : [];
        for (const o of items) {
            if (!o?.book) continue;
            const book = o.book as BibleBook;
            totals[book] = (totals[book] || 0) + (o.numVerses || 0);
        }
        if (items.length === 0 && outline?.book) {
            const book = outline.book as BibleBook;
            totals[book] = outline.numVerses || 0;
        }
        return totals;
    }, [outlineData?.items, outline?.book, outline?.numVerses]);

	// Highlighted child (immediate child of current node that contains the passage)
	const highlightedChildNode = useMemo(() => {
		if (!currentNode?.children) return undefined;
		return currentNode.children.find(child => containsPassage(child, highlightedPassageId));
	}, [currentNode, highlightedPassageId]);

    const breadcrumbPath = useMemo(() => {
        if (!currentNode) return [] as OutlineNode[];
        return findPathToNode(outline.structure, currentNode.id);
    }, [outline.structure, currentNode]);

    // Ref for the main container to attach gesture handlers
	const containerRef = useRef<HTMLDivElement>(null);

    useZoomGestures({
        containerRef,
        canZoomOut: canZoomOut,
        onZoomOut: onNavigateToParent,
        onZoomIn: onNavigateBackIn
    });

    // Transition anchor for BookRail -> Icicle right column
    const [transitionNodeId, setTransitionNodeId] = useState<string | undefined>(undefined);
    const handleZoomOut = useCallback(() => {
        const id = highlightedChildNode?.id;
        if (id) setTransitionNodeId(id);
        onNavigateToParent();
    }, [highlightedChildNode?.id, onNavigateToParent]);
    const handleTransitionDone = useCallback(() => setTransitionNodeId(undefined), []);

    // Debug: after navigation (currentNode change), sample target left-column rect after paint
    useEffect(() => {
        if (!currentNode?.id) return;
        const id = currentNode.id;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                try {
                    const target = document.querySelector(`[data-node-id="${id}"]`) as HTMLElement | null;
                    if (target) console.log('[Context ZoomOut] target after nav (effect 2raf)', id, target.getBoundingClientRect());
                    else console.log('[Context ZoomOut] target after nav (effect 2raf) not found', id);
                } catch {}
            });
        });
    }, [currentNode?.id]);

    return (
        hasCurrentNode ? (
            <div
                ref={containerRef}
                className="relative w-full overflow-hidden bg-cover bg-center py-4"
                style={{
                    backgroundImage: `url(${hero7})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    height: 'calc(100vh - 48px)',
                    touchAction: 'none',
                    overflow: 'hidden'
                }}
            >
                {/* Dark overlay */}
                <div className={cn(
                    "absolute inset-0 transition-opacity duration-300",
                    isDarkMode ? "bg-black/60" : "bg-black/40"
                )} />

                {/* Content Area */}
                <div className={cn(
                    "w-full h-full max-w-full min-w-0 flex items-center",
                )}>
                    <LayoutGroup id="canon-context">
                    <motion.div 
                        className="w-full h-full flex gap-2 md:gap-4 items-stretch"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Canon rail - always visible */}
                        <div className="pl-1 md:pl-1 flex flex-col gap-1.5 h-full mb-4">
                            <div className="text-[10px] text-white/80 text-center">Bible</div>
                            <div className="flex-1 min-h-0 flex items-stretch">
                                <CanonRail
                                    currentBook={outline.book as BibleBook}
                                    isDarkMode={isDarkMode}
                                    onSelect={onBookSelect}
                                    bookVerseTotals={canonVerseTotals}
                                />
                            </div>
                        </div>
                        {/* Book rail - always visible */}
                        <div className="flex flex-col gap-1.5 h-full mb-4">
                            <div className="text-[10px] text-white/80 text-center">Book</div>
                            <div className="flex-1 min-h-0 flex items-stretch">
                                <BookRail
                                    outline={outline}
                                    currentNode={currentNode}
                                    highlightedNode={highlightedChildNode}
                                    isDarkMode={isDarkMode}
                                    onNodeClick={(n) => onNavigateToNodeContext(n.id)}
                                    focusedNode={currentNode}
                                    mode="focused"
                                    transitionNodeId={transitionNodeId}
                                />
                            </div>
                        </div>
                        <div className="flex-1 flex flex-col gap-3 min-w-0 pr-2 md:pr-4 pt-1">
                            <ContextBreadcrumbs
                                bookName={outline.book}
                                breadcrumbPath={breadcrumbPath}
                                canZoomOut={canZoomOut}
                                onNavigateToNode={onNavigateToNode}
                                onNavigateToParent={onNavigateToParent}
                                onNavigateBackIn={onNavigateBackIn}
                                onClose={handleClose}
                                bookId={outline.id}
                            />
                            {/* Icicle and Preview container - aligned horizontally */}
                            <div className="flex gap-2 flex-1 overflow-hidden" ref={icicleContainerRef}>
                            <motion.div
                                className="flex-1"
                                layout
                                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            >
                                <IcicleChart
                                    outline={outline}
                                    focusedNode={currentNode}
                                    highlightedNode={highlightedChildNode}
                                    highlightedPassageId={highlightedPassageId}
                                    isDarkMode={isDarkMode}
                                    onNodeClick={(n) => onNavigateToNode(n.id)}
                                    onZoomOut={handleZoomOut}
                                    transitionNodeId={transitionNodeId}
                                    onTransitionDone={handleTransitionDone}
                                />
                            </motion.div>
                            </div>
                        </div>
                    </motion.div>
                    </LayoutGroup>
                </div>
            </div>
        ) : (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 to-gray-800">
                <div className="text-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/60 mx-auto" />
                    <p className="text-white/80 text-lg">Preparing context view...</p>
                </div>
            </div>
        )
    );
}



